import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logo from "assets/images/house_scan_logo.png";
import calculator from "assets/images/calculator.png";
import { formatCurrencyInEuro } from "./formatNumber";

const styles = StyleSheet.create({
  page: {
    padding: 30,
  },
  topbar: {
    marginTop: 15,
    marginBottom: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  logo: {
    height: 46,
    width: 210,
  },
  calculator: {
    width: 50,
    height: 50,
    borderRadius: 25,
    padding: 13,
    backgroundColor: "#EFF9FF",
  },
  calculatorLogo: {},
  section: {
    marginBottom: 25,
    backgroundColor: "#EFF9FF",
    paddingHorizontal: 34,
    paddingTop: 31,
    paddingBottom: 17,
    borderRadius: 20,
  },
  contentBox: {
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "semibold",
    marginBottom: 10,
    color: "#000000",
  },
  content: {
    fontSize: 24,
    fontWeight: "semibold",
    color: "#1DA1F2",
  },
  description: {
    fontSize: 24,
    color: "#969696",
    fontWeight: "light",
    marginTop: 6,
  },
  bottomSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  buttonBox: {
    marginTop: 20,
  },
  buttonLabel: {
    fontWeight: "semibold",
    fontSize: 40,
    color: "#000000",
    textAlign: "center",
    width: 400,
  },
  button: {
    backgroundColor: "#1DA1F2",
    marginTop: 20,
    borderRadius: 10,
    width: 400,
    height: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  buttonText: {
    fontSize: 32,
    fontWeight: "normal",
    color: "#FFFFFF",
  },
  totalLabel: {
    color: "#969696",
    fontSize: 22,
    fontWeight: "normal",
    marginTop: 8,
  },
});

export const PdfReport = ({ reportData }) => {
  const mortgageAndLandTax =
    reportData.impostaIpotecaria + reportData.impostaCatastale;
  const totalPercentage =
    (reportData?.speseTotali / reportData.homePriceNum) * 100;

  return (
    <Document>
      <Page style={styles.page} size={"A4"} wrap={false}>
        <View style={styles.topbar}>
          <Image style={styles.logo} src={logo} />
          <View style={styles.calculator}>
            <Image style={styles.calculatorLogo} src={calculator} />
          </View>
        </View>
        <View style={styles.section}>
          <View style={styles.contentBox}>
            <Text style={styles.title}>Valore Immobile</Text>
            <Text style={styles.content}>
              {formatCurrencyInEuro(reportData.propertyValue)}€
            </Text>
          </View>

          {reportData.isRealStateAgency && (
            <View style={styles.contentBox}>
              <Text style={styles.title}>Onorario Agenzia Immobiliare</Text>
              <Text style={styles.content}>
                {formatCurrencyInEuro(reportData.costoAgenzia)}€
              </Text>
              <Text style={styles.description}>
                {" "}
                <Text style={{ fontWeight: "bold" }}>3,4% </Text>è la media
                nazionale sui costi di intermediazione a cui va sommata l’IVA
              </Text>
            </View>
          )}
          {reportData.impostaRegistro > 0 && (
            <View style={styles.contentBox}>
              <Text style={styles.title}>Imposta di Registro</Text>
              <Text style={styles.content}>
                {formatCurrencyInEuro(reportData.impostaRegistro)}€
              </Text>
              <Text style={styles.description}>
                Nel caso di acquisto da privato, l'imposta di registro è pari al
                2% del valore catastale dell'immobile per la prima casa e al 9%
                del valore catastale per altri immobili; nel caso di acquisto da
                impresa, l'importo è sempre pari a 200 euro. Il valore catastale
                dell'immobile è pari a 115,5 volte la rendita catastale per la
                prima casa, 126 volte la rendita catastale per altri immobili.
              </Text>
            </View>
          )}

          <View style={styles.contentBox}>
            <Text style={styles.title}>Imposta Ipotecaria e Catastale</Text>
            <Text style={styles.content}>
              {formatCurrencyInEuro(mortgageAndLandTax)}€
            </Text>
            <Text style={styles.description}>
              L’imposta ipotecaria come quella catastale è pari a 50€. Se
              acquisti da una impresa di costruzione saranno 200€ ciascuna.
            </Text>
          </View>

          <View style={styles.contentBox}>
            <Text style={styles.title}>
              Onori Notarili per atto di Compravendita
            </Text>
            <Text style={styles.content}>
              {formatCurrencyInEuro(reportData.parcellaNotaio)}€
            </Text>
            <Text style={styles.description}>
              La parcella media per l’atto di compravendita oscilla tra 1500 e
              3000€ a cui va sommata l’IVA al 22%
            </Text>
          </View>

          {reportData.iva > 0 && (
            <View style={styles.contentBox}>
              <Text style={styles.title}>IVA - Acquisto da Impresa</Text>
              <Text style={styles.content}>
                {formatCurrencyInEuro(reportData.iva)}€
              </Text>
              <Text style={styles.description}>
                Nel caso di acquisto da un’impresa di costruzioni, bisognerà
                pagare l’IVA.Rispettivamente al 4% nel caso acquisti come prima
                casa, e 10% come seconda casa
              </Text>
            </View>
          )}
        </View>

        {reportData.lendNum > 0 && (
          <View style={styles.section}>
            <View style={styles.contentBox}>
              <Text style={styles.title}>Istruttoria Pratica di Mutuo</Text>
              <Text style={styles.content}>
                {formatCurrencyInEuro(reportData.istruttoriaMutuo)}€
              </Text>
              <Text style={styles.description}>
                L'istruttoria è la fase in cui la Banca tramite una raccolta
                documentale analizza il merito creditizio del richiedente e
                l'adeguatezza dell'immobile a fungere come garanzia del
                finanziamento. II costo dell'istruttoria medio è di 250€
              </Text>
            </View>
            <View style={styles.contentBox}>
              <Text style={styles.title}>Perizia dell'immobile</Text>
              <Text style={styles.content}>
                {formatCurrencyInEuro(reportData.periziaImmobile)}€
              </Text>
              <Text style={styles.description}>
                La perizia è effettuata da un tecnico incaricato dalla Banca che
                accerta il valore dell'immobile e l'assenza di gravami o
                irregolarità. Il costo della perizia medio è di 200€
              </Text>
            </View>
            <View style={styles.contentBox}>
              <Text style={styles.title}>Imposta Sostitutiva </Text>
              <Text style={styles.content}>
                {formatCurrencyInEuro(reportData.impostaSostitutiva)}€
              </Text>
              <Text style={styles.description}>
                L'imposta sostitutiva è pari allo 0,25% dell'importo del mutuo
                tranne per l'acquisto di una seconda casa nel quale caso è pari
                al 2% dell'importo del mutuo
              </Text>
            </View>
            <View style={styles.contentBox}>
              <Text style={styles.title}>
                Onori Notarili per atto di Mutuo{" "}
              </Text>
              <Text style={styles.content}>
                {formatCurrencyInEuro(reportData.parcellaNotaioMutuo)}€
              </Text>
              <Text style={styles.description}>
                Gli onorari notarili sono variabili in base alla località,
                all'importo del mutuo e alla percentuale di iscrizione
                ipotecaria richiesta dalla banca (nell'esempio si ipotizza una
                iscrizione ipotecaria pari al 200% dell'importo del mutuo); gli
                onorari notarili sono soggetti a IVA del 22%
              </Text>
            </View>
          </View>
        )}
        <View style={styles.bottomSection}>
          <View style={styles.buttonBox}>
            <Text style={styles.buttonLabel}>Liquidità Necessaria</Text>
            <View style={styles.button}>
              <Text style={styles.buttonText}>
                {formatCurrencyInEuro(
                  reportData.homePriceNum +
                    reportData?.speseTotali -
                    reportData.lendNum
                )}
                €
              </Text>
            </View>
          </View>
          <View style={styles.buttonBox}>
            <Text style={styles.buttonLabel}>
              Prezzo Casa + Spese di Acquisto
            </Text>
            <View style={styles.button}>
              <Text style={styles.buttonText}>
                {formatCurrencyInEuro(
                  reportData.homePriceNum + reportData?.speseTotali
                )}
                €
              </Text>
            </View>
          </View>
          <View style={styles.buttonBox}>
            <Text style={styles.buttonLabel}>Spese di Acquisto</Text>
            <View style={styles.button}>
              <Text style={styles.buttonText}>
                {formatCurrencyInEuro(reportData?.speseTotali)}€
              </Text>
            </View>
          </View>
          <Text style={styles.totalLabel}>
            <Text style={{ fontWeight: "bold" }}>
              {totalPercentage.toFixed(2)}%{" "}
            </Text>
            di incidenza sul prezzo totale
          </Text>
        </View>
      </Page>
    </Document>
  );
};
